import { Campaign } from '../../types';
import { AppDispatch } from '../../store';
import { setIsCampaignCreated } from './campaignsSlice';
import { setLoading, setError, setSuccess } from '../app/appSlice';
import { CampaignController, CampaignPayload, CampaignsRequestPayload } from '../../api/controllers/campaign.controller';
import { ConfigController } from '../../api/controllers/config.controller';
import { setCampaign, setCampaigns, setCampaignObjective, setPricingConfig } from './campaignsSlice';
import { trackEvent, ANALYTICS_EVENTS } from '../../lib/posthog';

export const getCampaignObjective = () => async (dispatch: AppDispatch) => {
    try {
      const response = await CampaignController.getCampaignObjective();

      dispatch(setCampaignObjective(response.data));
    } catch (error: any) {
      dispatch(setError('Fetching campaigns failed. please try it again later'));
    }
  };

export const fetchPricingConfig = () => async (dispatch: AppDispatch) => {
  try {
    const response = await ConfigController.getPricingConfig();
    dispatch(setPricingConfig(response.data.data));
  } catch (error: any) {
    console.error('Failed to fetch pricing config:', error);
  }
};

export const getCampaigns = (
  payload: CampaignsRequestPayload
) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(''));

    const response = await CampaignController.getCampaigns(payload);

    dispatch(setCampaigns(response.data));
    dispatch(setSuccess(true));
  } catch (error: any) {
    dispatch(setError('Fetching campaigns failed. please try it again later'));
    dispatch(setSuccess(false));
  } finally {
    dispatch(setLoading(false));
  }
};

export const getCampaign = (payload: { id: string }) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setLoading(true));
    dispatch(setError(''));

    const response = await CampaignController.getCampaign(payload);

    dispatch(setCampaign(response.data));
    dispatch(setSuccess(true));
  } catch (error: any) {
    dispatch(setError('Fetching campaigns failed. please try it again later'));
    dispatch(setSuccess(false));
  } finally {
    dispatch(setLoading(false));
  }
};

export const createCampaign = (payload: CampaignPayload) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setError(''));

    const response = await CampaignController.createCampaign(payload);

    let campaign = response.data;

    // Skip auto-activation for signed_retainers campaigns —
    // they must remain pending until the retainer document is mapped by Ops.
    const isSignedRetainers = payload.objective?.toLowerCase() === 'signed_retainers';

    if (campaign?.id && campaign.status !== 'active' && !isSignedRetainers) {
      try {
        const activationResponse = await CampaignController.updateCampaign({
          campaign_id: campaign.id,
          updated_data: { status: 'active' },
        });
        campaign = activationResponse.data ?? { ...campaign, status: 'active' };
      } catch (activationError) {
        // Don't optimistically set status - activation failed (possibly due to verification)
        console.warn('Campaign auto-activation failed:', activationError);
        // Keep campaign with its original status from creation
      }
    }

    dispatch(setCampaign(campaign));
    dispatch(setSuccess(true));
    dispatch(setIsCampaignCreated(true));
    trackEvent(ANALYTICS_EVENTS.CAMPAIGN_CREATED, {
      campaign_id: campaign?.id,
      objective: payload.objective,
      status: campaign?.status,
    });
    return campaign;
  } catch (error: any) {
    dispatch(setError('Creating a new campaign failed. please try it again later'));
    dispatch(setSuccess(false));
    return null;
  }
};

export const updateCampaign = (payload: { campaign_id: string, updated_data?: Partial<Campaign> }) => async (dispatch: AppDispatch) => {
  try {
    dispatch(setError(''));

    const response = await CampaignController.updateCampaign(payload);

    dispatch(setCampaign(response.data));
    dispatch(setSuccess(true));
    dispatch(setIsCampaignCreated(true));
    trackEvent(ANALYTICS_EVENTS.CAMPAIGN_UPDATED, {
      campaign_id: payload.campaign_id,
      updated_fields: Object.keys(payload.updated_data || {}),
    });
    return response.data;
  } catch (error: any) {
    const message = error?.response?.data?.error || 'Updating campaign failed. Please try again later.';
    dispatch(setError(message));
    dispatch(setSuccess(false));
    throw new Error(message);
  }
};
